import { FunctionComponent, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Box, Button, Typography } from '@black-bear-energy/rucksack'
import {
  ChecklistCategoryAPIResponse,
  ChecklistDocument,
  ChecklistDocumentFilter,
  ChecklistDocumentSummary,
  SiteRFPDocumentSummary,
} from '@black-bear-energy/black-bear-energy-common'
import {
  isBBEUserOrSuperAdmin,
  useImpersonatingUserOrUser,
} from '../../state/modules/auth'
import { useDownloadSiteRFPDocumentDirectory } from '../../api/hooks/useDownloadSiteRFPDocumentDirectory'
import { useSiteRFPDocumentExchangeDetails } from '../../api/hooks/useSiteRFPDocumentExchangeDetails'
import { useSiteRFPDocumentSummary } from '../../api/hooks/useSiteRFPDocumentSummary'
import { useSiteRFPChecklists } from '../../api/hooks/useSiteRFPChecklists'
import { ConstructionDetailCard } from './ConstructionDetailCard'
import { ContractsDetailCard } from './ContractsDetailCard'
import { DocumentCircularProgress } from './DocumentCircularProgress'
import { ChecklistTable } from './ChecklistTable'
import { SiteRFPDetailCard } from './SiteRFPDetailCard'
import { DocumentFilterCounts } from '../helpers/DocumentExchange/DocumentFilterCounts'
import { checklistChildIsChecklist } from '../helpers/DocumentExchange/checklistChildIsDocument'

interface SiteRFPChecklistPageProps {
  category: ChecklistCategoryAPIResponse
}

export const SiteRFPChecklistPage: FunctionComponent<
  SiteRFPChecklistPageProps
> = ({ category }) => {
  const { siteRFPId } = useParams<{
    customerRFPId: string
    siteRFPId: string
  }>()
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState(ChecklistDocumentFilter.All)
  const { data: siteRFPDocumentSummary } = useSiteRFPDocumentSummary(
    Number(siteRFPId),
    category
  )
  const { data: siteRFPDocumentExchangeDetails } =
    useSiteRFPDocumentExchangeDetails(Number(siteRFPId))
  const { isPending: isPendingChecklists, data } = useSiteRFPChecklists(
    Number(siteRFPId),
    search,
    filter,
    category
  )
  const { records: siteRFPChecklists } = data ?? {}
  const { mutate: downloadSiteRFPDocumentDirectory } =
    useDownloadSiteRFPDocumentDirectory()

  const user = useImpersonatingUserOrUser()

  const allDocuments = siteRFPChecklists?.flatMap(getAllDocuments) ?? []
  const hasAtLeastOneUpload = allDocuments.some((doc) => doc.uploaded)
  const showDownloadAll =
    user && isBBEUserOrSuperAdmin(user) && hasAtLeastOneUpload

  const handleDownloadAllClick = () => {
    if (!siteRFPId || !siteRFPDocumentSummary?.name) {
      return
    }

    downloadSiteRFPDocumentDirectory({
      siteRFPId: Number(siteRFPId),
      siteRFPName: siteRFPDocumentSummary.name,
    })
  }

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2 }}>
        {category === ChecklistCategoryAPIResponse.Construction ? (
          <ConstructionDetailCard
            constructionName={siteRFPDocumentExchangeDetails?.constructionName}
            constructionStartDate={
              siteRFPDocumentExchangeDetails?.constructionStartDate
            }
            commercialOperationDate={
              siteRFPDocumentExchangeDetails?.commercialOperationDate
            }
            commercialOperationDateEstimate={
              siteRFPDocumentExchangeDetails?.commercialOperationDateEstimate
            }
            constructionStatus={
              siteRFPDocumentExchangeDetails?.constructionStatus
            }
          />
        ) : (
          <ContractsDetailCard
            contractsName={siteRFPDocumentExchangeDetails?.contractsName}
            contractStructure={
              siteRFPDocumentExchangeDetails?.contractStructure
            }
            contractExecutionDate={
              siteRFPDocumentExchangeDetails?.contractExecutionDate
            }
            contractExecutionDateEstimate={
              siteRFPDocumentExchangeDetails?.contractExecutionDateEstimate
            }
          />
        )}
        <SiteRFPDetailCard>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1">Documents</Typography>
            {showDownloadAll && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleDownloadAllClick}
              >
                Download All
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <DocumentCircularProgress
              variant="required"
              numerator={siteRFPDocumentSummary?.required?.numerator}
              denominator={siteRFPDocumentSummary?.required?.denominator}
            />
            <DocumentCircularProgress
              variant="verified"
              numerator={siteRFPDocumentSummary?.verified?.numerator}
              denominator={siteRFPDocumentSummary?.verified?.denominator}
            />
            <DocumentCircularProgress
              variant="additional"
              numerator={siteRFPDocumentSummary?.additional?.numerator}
              denominator={siteRFPDocumentSummary?.additional?.denominator}
            />
          </Box>
        </SiteRFPDetailCard>
      </Box>
      <ChecklistTable
        checklists={siteRFPChecklists}
        counts={getFilterCounts(siteRFPDocumentSummary)}
        isPending={isPendingChecklists}
        search={search}
        setSearch={setSearch}
        filter={filter}
        setFilter={setFilter}
      />
    </>
  )
}

function getAllDocuments(
  checklist: ChecklistDocumentSummary
): ChecklistDocument[] {
  return checklist.children.flatMap((child) =>
    checklistChildIsChecklist(child) ? getAllDocuments(child) : child
  )
}

function getFilterCounts(
  summary?: SiteRFPDocumentSummary
): DocumentFilterCounts {
  return {
    all: summary
      ? summary.required.denominator + summary.additional.denominator
      : null,
    requiredMissing: summary
      ? summary.required.denominator - summary.required.numerator
      : null,
    reviewMissing: summary ? summary.pendingReview : null,
    verified: summary ? summary.verified.numerator : null,
  }
}
